import React, { useState, useRef } from "react";
import { Modal } from "./Modal";
import { Tabs } from "../tabs/Tabs";
import { useProfile } from "../../hooks/useProfile";
import {
  useUpdateProfile,
  useChangePassword,
  useDeleteAccount,
  useUploadAvatar,
} from "../../hooks/useProfileSettings";
import { useTags, useCreateTag, useDeleteTag, useTagCounts } from "../../hooks/useTags";
import { Tag } from "../tag/Tag";
import { Table } from "../table/Table";
import { useToastStore } from "../../stores/toastStore";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { logger } from "../../lib/logger";
import type { Tag as TagType } from "../../types/orderbook";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const deleteAccount = useDeleteAccount();
  const uploadAvatar = useUploadAvatar();
  const showToast = useToastStore((s) => s.show);

  const [activeTab, setActiveTab] = useState<"profile" | "security" | "tags" | "danger">("profile");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Tags management
  const { data: tags = [] } = useTags();
  const { data: tagCounts = {} } = useTagCounts();
  const createTagMutation = useCreateTag();
  const deleteTagMutation = useDeleteTag();
  const [newTagName, setNewTagName] = useState("");

  // Profile form state
  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [username, setUsername] = useState(profile?.username || "");
  const [email, setEmail] = useState(profile?.email || "");

  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update form when profile loads
  React.useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setUsername(profile.username || "");
      setEmail(profile.email || "");
    }
  }, [profile]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile.mutateAsync({
        full_name: fullName.trim() || null,
        username: username.trim() || null,
        email: email,
      });
      showToast({ title: "Profile updated", type: "success" });
    } catch (error) {
      showToast({
        title: "Failed to update profile",
        type: "error",
      });
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast({ title: "Passwords don't match", type: "error" });
      return;
    }
    if (newPassword.length < 6) {
      showToast({ title: "Password must be at least 6 characters", type: "error" });
      return;
    }
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      showToast({ title: "Password changed", type: "success" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      showToast({
        title: error instanceof Error ? error.message : "Failed to change password",
        type: "error",
      });
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadAvatar.mutateAsync(file);
      showToast({ title: "Avatar updated", type: "success" });
    } catch (error) {
      showToast({
        title: error instanceof Error ? error.message : "Failed to upload avatar",
        type: "error",
      });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount.mutateAsync();
      showToast({ title: "Account deleted", type: "success" });
      onClose();
    } catch (error) {
      showToast({
        title: error instanceof Error ? error.message : "Failed to delete account",
        type: "error",
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Settings"
        subtitle="Manage your account settings"
      >
        <div className="ob-settings">
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(value) => setActiveTab(value as "profile" | "security" | "tags" | "danger")}
            options={[
              { value: "profile", label: "Profile" },
              { value: "security", label: "Security" },
              { value: "tags", label: "Tags" },
              { value: "danger", label: "Danger Zone" }
            ]}
          />

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="ob-settings-content">
              <form id="settings-profile-form" className="ob-form" onSubmit={handleUpdateProfile}>
                {/* Avatar */}
                <div className="ob-field">
                  <label htmlFor="avatar">Avatar</label>
                  <div className="ob-settings-avatar">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Avatar"
                        className="ob-settings-avatar-img"
                      />
                    ) : (
                      <div className="ob-settings-avatar-placeholder">
                        {profile?.full_name?.[0] || profile?.email?.[0] || "?"}
                      </div>
                    )}
                    <button
                      type="button"
                      className="ob-button ob-button-ghost ob-button-small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadAvatar.isPending}
                    >
                      {uploadAvatar.isPending ? "Uploading..." : "Change"}
                    </button>
                    <input
                      id="avatar"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>

                {/* Full Name */}
                <div className="ob-field">
                  <label htmlFor="full-name">Full Name</label>
                  <input
                    id="full-name"
                    type="text"
                    className="ob-input"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>

                {/* Username */}
                <div className="ob-field">
                  <label htmlFor="username">Username</label>
                  <input
                    id="username"
                    type="text"
                    className="ob-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="username"
                  />
                </div>

                {/* Email */}
                <div className="ob-field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    className="ob-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="ob-settings-actions">
                  <button
                    type="button"
                    className="ob-button ob-button-ghost"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ob-button ob-button-primary"
                    disabled={updateProfile.isPending}
                  >
                    {updateProfile.isPending ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <div className="ob-settings-content">
              <form id="settings-security-form" className="ob-form" onSubmit={handleChangePassword}>
                <div className="ob-field">
                  <label htmlFor="current-password">Current Password</label>
                  <input
                    id="current-password"
                    type="password"
                    className="ob-input"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="ob-field">
                  <label htmlFor="new-password">New Password</label>
                  <input
                    id="new-password"
                    type="password"
                    className="ob-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                <div className="ob-field">
                  <label htmlFor="confirm-password">Confirm New Password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    className="ob-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                <div className="ob-settings-actions">
                  <button
                    type="button"
                    className="ob-button ob-button-ghost"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ob-button ob-button-primary"
                    disabled={changePassword.isPending}
                  >
                    {changePassword.isPending ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tags Tab */}
          {activeTab === "tags" && (
            <div className="ob-settings-content">
              <form
                className="ob-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newTagName.trim()) return;
                  
                  try {
                    await createTagMutation.mutateAsync({
                      name: newTagName.trim(),
                      color: null
                    });
                    setNewTagName("");
                    showToast({ title: "Tag created", type: "success" });
                  } catch (error) {
                    logger.error("Error creating tag", error);
                    const errorMessage = error instanceof Error ? error.message : "Failed to create tag";
                    showToast({
                      title: errorMessage.includes("already exists") ? "Tag already exists" : "Failed to create tag",
                      type: "error"
                    });
                  }
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--ob-space-2)" }}>
                  <label htmlFor="new-tag-name" style={{ fontSize: "var(--ob-font-size-sm)", color: "var(--ob-color-text-muted)" }}>Create New Tag</label>
                  <div style={{ display: "flex", gap: "var(--ob-space-2)" }}>
                    <input
                      id="new-tag-name"
                      type="text"
                      className="ob-input"
                      style={{ flex: 1 }}
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Tag name"
                    />
                    <button
                      type="submit"
                      className="ob-button ob-button-primary"
                      disabled={!newTagName.trim() || createTagMutation.isPending}
                    >
                      {createTagMutation.isPending ? "Creating..." : "Create"}
                    </button>
                  </div>
                </div>
              </form>

              {tags.length === 0 ? (
                <p className="ob-field-description">No tags yet. Create your first tag above.</p>
              ) : (
                <Table
                  columns={[
                    { key: "tag", label: "Tag", align: "left" },
                    { key: "items", label: "Items", align: "left" },
                    { key: "actions", label: "Actions", align: "right" }
                  ]}
                  rows={tags.map((tag) => ({
                    tag: <Tag tag={tag} />,
                    items: <span style={{ color: "var(--ob-color-text-muted)" }}>{tagCounts[tag.id] || 0}</span>,
                    actions: (
                      <button
                        type="button"
                        className="ob-button ob-button-ghost ob-button-small"
                        onClick={async () => {
                          if (confirm(`Delete tag "${tag.name}"? This will remove it from all items.`)) {
                            try {
                              await deleteTagMutation.mutateAsync(tag.id);
                              showToast({ title: "Tag deleted", type: "success" });
                            } catch (error) {
                              logger.error("Error deleting tag", error);
                              showToast({ title: "Failed to delete tag", type: "error" });
                            }
                          }
                        }}
                        disabled={deleteTagMutation.isPending}
                      >
                        Delete
                      </button>
                    )
                  }))}
                  showHeader={false}
                />
              )}
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === "danger" && (
            <div className="ob-settings-content">
              <div className="ob-settings-danger">
                <div className="ob-settings-danger-header">
                  <h3 className="ob-settings-danger-title">Delete Account</h3>
                  <p className="ob-settings-danger-text">
                    Once you delete your account, there is no going back. Please be certain. This action is irreversible.
                  </p>
                </div>
                <button
                  type="button"
                  className="ob-button ob-button-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteAccount.isPending}
                >
                  {deleteAccount.isPending ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        itemName="your account"
        loading={deleteAccount.isPending}
      />
    </>
  );
};
